import { useState, useMemo } from 'react'
import {
  Plus, ChevronLeft, ChevronRight,
  RotateCcw, Search, Layers, Download,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TransientStaysTable, TransientStayMutateDrawer, DeleteTransientStayDialog, CancelTransientStayDialog, NoShowTransientStayDialog, CheckOutTransientStayDialog, TerminateTransientStayDialog, TodayView, UpcomingView, TransientStayDetailPanel } from '@/components/transient-stays'
import { SavedViewTabs, FilterPill, ColumnGroupsDropdown } from '@/components/data-table'
import {
  useTransientStays,
  useDeleteTransientStay,
  useUpdateTransientStay,
  useCancelTransientStay,
  useCheckOutTransientStay,
  useNoShowTransientStay,
  useTerminateTransientStay,
} from '@/hooks/useTransientStays'
import { useTransientViewStore } from '@/stores/transientViewStore'
import { useAddPlaneWizardStore } from '@/stores/addPlaneWizardStore'
import { AddPlaneWizard } from '@/components/ramp-wizard'
import { useAppContext } from '@/hooks/routing'
import {
  TRANSIENT_SAVED_VIEWS,
  TRANSIENT_STATUS_FILTER_OPTIONS,
  SPACE_TYPE_FILTER_OPTIONS,
  TRANSIENT_COLUMN_GROUPS,
  TRANSIENT_GROUPING_OPTIONS,
  TRANSIENT_STATUS_LABELS,
  INVOICE_STATUS_LABELS,
  deriveSpaceType,
  getInvoiceStatus,
  type TransientTimeTab,
  type TransientStatus,
  type SpaceType,
  type TransientGroupingOption,
  type TransientStayTableRow,
  getRecentCheckouts,
  categorizeTodayTransients,
  getUpcomingTransients,
} from '@/types/transient-stays'

/** Export transient stays data to CSV */
function exportTransientsCsv(data: TransientStayTableRow[]) {
  const headers = [
    'Booking #', 'Status', 'Client', 'Tail #', 'Aircraft Type',
    'Unit', 'Building', 'Check In', 'Departure', 'Nights',
    'Rate', 'Total', 'Invoice Status',
  ]

  const rows = data.map((r) => {
    const invoiceStatus = getInvoiceStatus(r)
    return [
      r.booking_number,
      TRANSIENT_STATUS_LABELS[r.status] || r.status || '',
      r.client_name || '',
      r.tail_number || '',
      r.aircraft_type_name || '',
      r.unit_code || '',
      r.building_name || '',
      r.check_in || '',
      r.actual_check_out || r.expected_check_out || '',
      r.nights_count?.toString() || '',
      r.rate_amount?.toString() || '',
      r.total_amount?.toString() || '',
      invoiceStatus ? INVOICE_STATUS_LABELS[invoiceStatus] : '',
    ]
  })

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell: string) => `"${cell}"`).join(','))
    .join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `transient_stays_${new Date().toISOString().split('T')[0]}.csv`
  link.click()
}

/**
 * Transient Stays management page (v2.1)
 *
 * Compact 2-row header:
 * - Row 1: Title + Saved View Tabs + [Date Nav] + Action buttons
 * - Row 2 (table tabs only): Search + Quick Filter Pills + Column Groups + Grouping + Export
 *
 * Hybrid layout:
 * - Today/Upcoming: Purpose-built operational views (no Row 2)
 * - Checked Out/All: Standard table with full controls
 */
export function TransientStaysPage() {
  const { locationId, locationName, scope, locations } = useAppContext()
  const { openWizard } = useAddPlaneWizardStore()

  const {
    activeTab,
    setActiveTab,
    quickFilters,
    setStatusFilter,
    setSpaceTypeFilter,
    setLocationFilter,
    resetFiltersToTabDefaults,
    getIsModified,
    selectedStayId,
    setSelectedStayId,
    search,
    setSearch,
    groupBy,
    setGroupBy,
    visibleColumnGroups,
    toggleColumnGroup,
  } = useTransientViewStore()

  // Date picker state for Today view
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  // Sheet/Dialog state
  const [sheetState, setSheetState] = useState<{
    open: boolean
    transientStayId?: string
  }>({ open: false })
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    bookingNumber: string
  } | null>(null)
  const [cancelTarget, setCancelTarget] = useState<{
    id: string
    bookingNumber: string
  } | null>(null)
  const [checkOutTarget, setCheckOutTarget] = useState<{
    id: string
    bookingNumber: string
  } | null>(null)
  const [noShowTarget, setNoShowTarget] = useState<{
    id: string
    bookingNumber: string
  } | null>(null)
  const [terminateTarget, setTerminateTarget] = useState<{
    id: string
    bookingNumber: string
  } | null>(null)

  // Fetch all transient stays
  const { data: transientStays, isLoading } = useTransientStays()

  // Mutations
  const deleteTransientStay = useDeleteTransientStay()
  const updateTransientStay = useUpdateTransientStay()
  const cancelTransientStay = useCancelTransientStay()
  const checkOutTransientStay = useCheckOutTransientStay()
  const noShowTransientStay = useNoShowTransientStay()
  const terminateTransientStay = useTerminateTransientStay()

  const isModified = getIsModified()

  // Show table tabs (checked_out / all)
  const isTableTab = activeTab === 'checked_out' || activeTab === 'all'

  // Calculate counts per tab
  const tabCounts = useMemo(() => {
    if (!transientStays) return { today: 0, upcoming: 0, checked_out: 0, all: 0 } as Record<TransientTimeTab, number>

    const todaySections = categorizeTodayTransients(transientStays, selectedDate)
    const todayCount = todaySections.arriving.length + todaySections.onGround.length

    return {
      today: todayCount,
      upcoming: getUpcomingTransients(transientStays, 14).length,
      checked_out: getRecentCheckouts(transientStays, 30).length,
      all: transientStays.length,
    } as Record<TransientTimeTab, number>
  }, [transientStays, selectedDate])

  // Filter data based on active tab + quick filters
  const filteredData = useMemo(() => {
    if (!transientStays) return []

    let result: typeof transientStays

    switch (activeTab) {
      case 'checked_out':
        result = getRecentCheckouts(transientStays, 30)
        break
      case 'all':
        result = transientStays
        break
      default:
        return transientStays
    }

    // Apply quick filters (only for table tabs)
    if (quickFilters.status.length > 0) {
      result = result.filter((t) => quickFilters.status.includes(t.status))
    }
    if (quickFilters.spaceType.length > 0) {
      result = result.filter((t) => {
        const space = deriveSpaceType(t.unit_type)
        return space !== null && quickFilters.spaceType.includes(space)
      })
    }
    if (quickFilters.locationIds.length > 0) {
      result = result.filter((t) => quickFilters.locationIds.includes(t.location_id))
    }

    return result
  }, [transientStays, activeTab, quickFilters])

  // Selected stay for detail panel
  const selectedStay = useMemo(() => {
    if (!selectedStayId || !transientStays) return null
    return transientStays.find((t) => t.id === selectedStayId) ?? null
  }, [selectedStayId, transientStays])

  // Handlers
  const handleNewBooking = () => {
    openWizard({
      source: 'transient',
      locationId: locationId || undefined,
    })
  }

  const handleEdit = (id: string) => {
    setSelectedStayId(null)
    setSheetState({ open: true, transientStayId: id })
  }

  const handleDelete = (id: string, bookingNumber: string) => {
    setDeleteTarget({ id, bookingNumber })
  }

  const handleRowClick = (id: string) => {
    setSelectedStayId(id)
  }

  const handleCheckIn = async (id: string) => {
    await updateTransientStay.mutateAsync({
      id,
      data: { status: 'checked_in' },
    })
  }

  const handleCheckOut = async (id: string) => {
    await updateTransientStay.mutateAsync({
      id,
      data: {
        status: 'checked_out',
        actual_check_out: new Date().toISOString(),
      },
    })
  }

  // Table row action handlers (open confirmation dialogs)
  const handleTableCancel = (id: string, bookingNumber: string) => {
    setCancelTarget({ id, bookingNumber })
  }

  const handleTableCheckOut = (id: string, bookingNumber: string) => {
    setCheckOutTarget({ id, bookingNumber })
  }

  const handleTableNoShow = (id: string, bookingNumber: string) => {
    setNoShowTarget({ id, bookingNumber })
  }

  const handleTableTerminate = (id: string, bookingNumber: string) => {
    setTerminateTarget({ id, bookingNumber })
  }

  // Dialog confirm handlers
  const handleConfirmCancel = async (params: { cancellation_reason?: string }) => {
    if (cancelTarget) {
      await cancelTransientStay.mutateAsync({ id: cancelTarget.id, ...params })
      setCancelTarget(null)
    }
  }

  const handleConfirmCheckOut = async () => {
    if (checkOutTarget) {
      await checkOutTransientStay.mutateAsync(checkOutTarget.id)
      setCheckOutTarget(null)
    }
  }

  const handleConfirmNoShow = async () => {
    if (noShowTarget) {
      await noShowTransientStay.mutateAsync(noShowTarget.id)
      setNoShowTarget(null)
    }
  }

  const handleConfirmTerminate = async (params: { termination_reason?: string }) => {
    if (terminateTarget) {
      await terminateTransientStay.mutateAsync({ id: terminateTarget.id, ...params })
      setTerminateTarget(null)
    }
  }

  const handleSheetOpenChange = (open: boolean) => {
    setSheetState((prev) => ({
      open,
      transientStayId: open ? prev.transientStayId : undefined,
    }))
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteTarget) {
      await deleteTransientStay.mutateAsync(deleteTarget.id)
      setDeleteTarget(null)
    }
  }

  // Date navigation for Today view
  const handlePrevDay = () => {
    const newDate = new Date(selectedDate)
    newDate.setDate(newDate.getDate() - 1)
    setSelectedDate(newDate)
  }

  const handleNextDay = () => {
    const newDate = new Date(selectedDate)
    newDate.setDate(newDate.getDate() + 1)
    setSelectedDate(newDate)
  }

  const handleToday = () => {
    setSelectedDate(new Date())
  }

  const isToday = selectedDate.toDateString() === new Date().toDateString()

  const subtitle =
    scope === 'location' && locationName
      ? `at ${locationName}`
      : undefined

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Row 1: Title + Saved View Tabs + [Date Nav] + Action Buttons */}
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="flex items-baseline gap-2 shrink-0">
          <h1 className="text-2xl font-bold tracking-tight">Transient Stays</h1>
          {subtitle && (
            <span className="text-sm text-muted-foreground hidden sm:inline">
              {subtitle}
            </span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <SavedViewTabs
            views={TRANSIENT_SAVED_VIEWS}
            activeView={activeTab}
            onViewChange={setActiveTab}
            counts={tabCounts}
            isModified={isModified}
          />
        </div>

        {/* Date Navigator — Today tab only */}
        {activeTab === 'today' && (
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handlePrevDay}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant={isToday ? 'secondary' : 'outline'}
              size="sm"
              onClick={handleToday}
              className="h-8 min-w-[120px]"
            >
              {isToday
                ? 'Today'
                : selectedDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleNextDay}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" className="h-8" onClick={handleNewBooking}>
            <Plus className="mr-2 h-4 w-4" />
            New Booking
          </Button>
        </div>
      </div>

      {/* Row 2: Search + Quick Filter Pills + Toolbar — Table tabs only */}
      {isTableTab && (
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search booking#, client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-sm"
            />
          </div>

          <div className="h-4 w-px bg-border mx-1" />

          <FilterPill
            label="Status"
            options={TRANSIENT_STATUS_FILTER_OPTIONS}
            selected={quickFilters.status}
            onChange={(values) => setStatusFilter(values as TransientStatus[])}
          />
          <FilterPill
            label="Space"
            options={SPACE_TYPE_FILTER_OPTIONS}
            selected={quickFilters.spaceType}
            onChange={(values) => setSpaceTypeFilter(values as SpaceType[])}
          />
          {locations.length > 1 && (
            <FilterPill
              label="Location"
              options={locations.map((loc) => ({ value: loc.id, label: loc.name }))}
              selected={quickFilters.locationIds}
              onChange={(values) => setLocationFilter(values)}
            />
          )}
          {isModified && (
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFiltersToTabDefaults}
              className="h-7 gap-1.5 text-xs text-muted-foreground"
            >
              <RotateCcw className="h-3 w-3" />
              Reset
            </Button>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <ColumnGroupsDropdown
              groups={TRANSIENT_COLUMN_GROUPS}
              visibleGroups={visibleColumnGroups}
              onToggle={toggleColumnGroup}
            />

            <Select value={groupBy} onValueChange={(v) => setGroupBy(v as TransientGroupingOption)}>
              <SelectTrigger className="h-8 w-[140px]">
                <Layers className="mr-2 h-3.5 w-3.5" />
                <SelectValue placeholder="Group by..." />
              </SelectTrigger>
              <SelectContent>
                {TRANSIENT_GROUPING_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => exportTransientsCsv(filteredData as TransientStayTableRow[])}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Export CSV
            </Button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 flex-1">
          <div className="text-muted-foreground">Loading transient stays...</div>
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          {/* Today View */}
          {activeTab === 'today' && (
            <TodayView
              transients={transientStays || []}
              selectedDate={selectedDate}
              onCheckIn={handleCheckIn}
              onCheckOut={handleCheckOut}
              onView={handleRowClick}
            />
          )}

          {/* Upcoming View */}
          {activeTab === 'upcoming' && (
            <UpcomingView
              transients={transientStays || []}
              days={14}
              onView={handleRowClick}
            />
          )}

          {/* Checked Out / All — Table View */}
          {isTableTab && (
            <TransientStaysTable
              data={filteredData}
              isLoading={false}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onRowClick={handleRowClick}
              onCheckIn={handleCheckIn}
              onCheckOut={handleTableCheckOut}
              onCancel={handleTableCancel}
              onNoShow={handleTableNoShow}
              onTerminate={handleTableTerminate}
            />
          )}
        </div>
      )}

      {/* Footer Summary Bar */}
      {/* <TransientSummaryFooter transients={filteredData} activeTab={activeTab} /> */}

      {/* Detail Panel (Sheet or Dialog) */}
      <TransientStayDetailPanel
        stay={selectedStay}
        open={!!selectedStayId}
        onOpenChange={(open) => {
          if (!open) setSelectedStayId(null)
        }}
        onEdit={handleEdit}
        onCheckIn={handleCheckIn}
        onCheckOut={handleCheckOut}
      />

      {/* Add Plane Wizard (for creating new bookings) */}
      <AddPlaneWizard locationId={locationId || undefined} />

      {/* Edit Transient Stay Drawer (edit only — new bookings use wizard) */}
      <TransientStayMutateDrawer
        open={sheetState.open}
        onOpenChange={handleSheetOpenChange}
        transientStayId={sheetState.transientStayId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteTransientStayDialog
        open={!!deleteTarget}
        onOpenChange={handleCloseDeleteDialog}
        bookingNumber={deleteTarget?.bookingNumber ?? ''}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteTransientStay.isPending}
      />

      {/* Cancel Confirmation Dialog */}
      <CancelTransientStayDialog
        open={!!cancelTarget}
        onOpenChange={(open) => { if (!open) setCancelTarget(null) }}
        bookingNumber={cancelTarget?.bookingNumber ?? ''}
        onConfirm={handleConfirmCancel}
        isPending={cancelTransientStay.isPending}
      />

      {/* Check Out Confirmation Dialog */}
      <CheckOutTransientStayDialog
        open={!!checkOutTarget}
        onOpenChange={(open) => { if (!open) setCheckOutTarget(null) }}
        bookingNumber={checkOutTarget?.bookingNumber ?? ''}
        onConfirm={handleConfirmCheckOut}
        isPending={checkOutTransientStay.isPending}
      />

      {/* No-Show Confirmation Dialog */}
      <NoShowTransientStayDialog
        open={!!noShowTarget}
        onOpenChange={(open) => { if (!open) setNoShowTarget(null) }}
        bookingNumber={noShowTarget?.bookingNumber ?? ''}
        onConfirm={handleConfirmNoShow}
        isPending={noShowTransientStay.isPending}
      />

      {/* Terminate Confirmation Dialog */}
      <TerminateTransientStayDialog
        open={!!terminateTarget}
        onOpenChange={(open) => { if (!open) setTerminateTarget(null) }}
        bookingNumber={terminateTarget?.bookingNumber ?? ''}
        onConfirm={handleConfirmTerminate}
        isPending={terminateTransientStay.isPending}
      />
    </div>
  )
}
